import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { X, Sparkles, Loader2, CheckCircle } from "lucide-react";
import { BACKEND_URL } from "@/config/runtime";
import { useSidebar } from "@/context/SidebarContext";
import { useAuth } from "@/context/AuthContext";

export default function PromoSubscribeBanner() {
  const location = useLocation();
  const { isExpanded } = useSidebar();
  const { isAuthenticated, user } = useAuth();
  const [visible, setVisible] = useState(false);
  const [animateIn, setAnimateIn] = useState(false);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  // Check if user has a sidebar (authenticated vendor, camp, or admin)
  const hasSidebar = isAuthenticated && user && 
    (user.account_type === "vendor" || user.account_type === "camp" || user.account_type === "admin");

  // Determine current page type for page-specific dismissal
  const isSearchPage = location.pathname.includes("/search") || location.pathname.includes("/category");
  const pageKey = isSearchPage ? "search" : "home";
  const dismissKey = `promo_banner_dismissed_${pageKey}`;

  useEffect(() => {
    // Reset state when page changes
    setVisible(false);
    setAnimateIn(false);
    setSuccess(false);
    setError("");

    // Check if user has dismissed the banner for THIS specific page
    const dismissed = localStorage.getItem(dismissKey);
    const alreadySubscribed = localStorage.getItem("promo_subscribed");
    
    if (dismissed || alreadySubscribed) {
      return;
    }

    // Show banner after 2 seconds with animation
    const showTimer = setTimeout(() => {
      setVisible(true);
      // Trigger animation after a small delay for the element to mount
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setAnimateIn(true);
        });
      });
    }, 2000);

    return () => clearTimeout(showTimer);
  }, [location.pathname, dismissKey]);

  const handleDismiss = () => {
    setAnimateIn(false);
    // Wait for animation to complete before hiding
    setTimeout(() => {
      setVisible(false);
      // Save dismissal for THIS specific page only
      localStorage.setItem(dismissKey, "true");
    }, 300);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email.trim() || !email.includes("@")) {
      setError("Please enter a valid email");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const res = await fetch(`${BACKEND_URL}/api/promo-subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim(),
          source: isSearchPage ? "search_page" : "homepage_banner"
        })
      });

      const data = await res.json();

      if (res.ok) {
        setSuccess(true);
        localStorage.setItem("promo_subscribed", "true");
        // Auto-hide after showing success
        setTimeout(() => {
          handleDismiss();
        }, 3000);
      } else {
        setError(data.detail || "Something went wrong");
      }
    } catch (err) {
      setError("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  if (!visible) return null;

  return (
    <>
      {/* Mobile: Full width banner */}
      <div 
        className={`fixed z-50 transition-all duration-300 ease-out sm:hidden ${
          animateIn ? "translate-y-0 opacity-100" : "translate-y-full opacity-0"
        } bottom-0 right-0`}
        style={{
          left: hasSidebar ? (isExpanded ? '224px' : '64px') : '0px'
        }}
        data-testid="promo-subscribe-banner"
      >
        <div className="bg-gradient-to-r from-[#1E3A2F] to-[#2D5A3D] shadow-2xl border-t border-[#3D6B4A]">
          <div className="px-3 py-2.5">
            {success ? (
              <div className="flex items-center justify-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-400" />
                <p className="text-white font-medium text-sm">You're in!</p>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-[#FFD93D] flex-shrink-0" />
                <form onSubmit={handleSubmit} className="flex items-center gap-2 flex-1 min-w-0">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setError("");
                    }}
                    placeholder="Email for deals"
                    className={`flex-1 min-w-0 px-3 py-1.5 text-sm rounded-md bg-white/10 border ${
                      error ? "border-red-400" : "border-white/20"
                    } text-white placeholder-white/60 focus:outline-none focus:border-white/50`}
                    disabled={loading}
                    data-testid="promo-email-input"
                  />
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-3 py-1.5 bg-[#FFD93D] hover:bg-[#f5ce2d] text-[#1E3A2F] font-semibold text-sm rounded-md transition-colors disabled:opacity-70 flex-shrink-0"
                    data-testid="promo-submit-btn"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Go"}
                  </button>
                </form>
                <button
                  onClick={handleDismiss}
                  className="p-1 text-white/60 hover:text-white transition-colors flex-shrink-0"
                  aria-label="Dismiss"
                  data-testid="promo-dismiss-btn"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
            {error && <p className="text-red-300 text-xs mt-1 text-center">{error}</p>}
          </div>
        </div>
      </div>

      {/* Desktop: Centered floating pill with rounded edges */}
      <div 
        className={`fixed z-50 transition-all duration-300 ease-out hidden sm:block bottom-6`}
        style={{
          left: hasSidebar 
            ? `calc(${isExpanded ? '224px' : '64px'} + (100% - ${isExpanded ? '224px' : '64px'}) / 2)` 
            : '50%',
          transform: `translateX(-50%) translateY(${animateIn ? '0' : '2rem'})`,
          opacity: animateIn ? 1 : 0,
        }}
        data-testid="promo-subscribe-banner-desktop"
      >
        <div className="bg-gradient-to-r from-[#1E3A2F] to-[#2D5A3D] shadow-2xl rounded-full border border-[#3D6B4A]/50">
          <div className="px-6 py-3">
            {success ? (
              <div className="flex items-center justify-center gap-3">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <p className="text-white font-medium">You're in! Check your inbox for confirmation.</p>
              </div>
            ) : (
              <div className="flex items-center gap-4">
                {/* Left: Text */}
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#FFD93D] flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-4 h-4 text-[#1E3A2F]" />
                  </div>
                  <h3 className="text-white font-bold whitespace-nowrap">Get Exclusive Deals & Discounts</h3>
                </div>

                {/* Right: Form */}
                <form onSubmit={handleSubmit} className="flex items-center gap-2">
                  <div className="relative">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        setError("");
                      }}
                      placeholder="Enter your email"
                      className={`w-56 px-4 py-2 rounded-full bg-white/10 border ${
                        error ? "border-red-400" : "border-white/20"
                      } text-white placeholder-white/60 focus:outline-none focus:border-white/50 focus:bg-white/15 transition-colors`}
                      disabled={loading}
                      data-testid="promo-email-input-desktop"
                    />
                    {error && (
                      <p className="absolute -top-6 left-0 text-red-300 text-xs bg-red-900/80 px-2 py-1 rounded">{error}</p>
                    )}
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-5 py-2 bg-[#FFD93D] hover:bg-[#f5ce2d] text-[#1E3A2F] font-semibold rounded-full transition-colors disabled:opacity-70 flex items-center gap-2 whitespace-nowrap"
                    data-testid="promo-submit-btn-desktop"
                  >
                    {loading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      "Sign Up"
                    )}
                  </button>
                </form>

                {/* Close button */}
                <button
                  onClick={handleDismiss}
                  className="p-2 text-white/60 hover:text-white transition-colors"
                  aria-label="Dismiss"
                  data-testid="promo-dismiss-btn-desktop"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
